import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { storybookMediaUrl, type StorybookBook, type StorybookPage } from '@/lib/storybook-api';

interface StorybookCoverProps {
  book: StorybookBook;
  onMediaError?: () => void;
  /** Heading level for the title: h1 on the preview, h2 inside the reader. */
  headingLevel?: 1 | 2;
  className?: string;
}

/** The cover illustration with the title and "starring …" laid over it. */
export function StorybookCover({ book, onMediaError, headingLevel = 2, className }: StorybookCoverProps) {
  const { t } = useTranslation();
  const cover = storybookMediaUrl(book.coverUrl);
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  return (
    <figure
      className={cn(
        'ph-no-capture relative mx-auto aspect-square w-full max-w-xl overflow-hidden rounded-3xl bg-[linear-gradient(160deg,#28336e_0%,#5b4bb7_55%,#a77fe0_100%)] shadow-[0_20px_50px_rgba(32,41,74,0.25)]',
        className,
      )}
    >
      {cover ? (
        <img
          src={cover}
          alt={t('storybookPage.book.reader.coverAlt')}
          className="h-full w-full object-cover"
          onError={onMediaError}
        />
      ) : (
        <div aria-hidden="true" className="flex h-full items-center justify-center text-7xl">
          📖
        </div>
      )}
      <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-5 pb-5 pt-20 text-center text-white sm:px-8 sm:pb-7">
        <Heading className="text-2xl font-semibold leading-tight text-white drop-shadow sm:text-4xl">
          {book.title || t('storybookPage.book.untitled')}
        </Heading>
        {book.childName && (
          <p className="mt-1 text-sm font-medium text-white/90 sm:text-base">
            {t('storybookPage.book.preview.starring', { name: book.childName })}
          </p>
        )}
      </figcaption>
    </figure>
  );
}

interface StorybookPageTextProps {
  page: StorybookPage;
  className?: string;
}

/** One page of story text on warm "paper". */
export function StorybookPageText({ page, className }: StorybookPageTextProps) {
  const { t } = useTranslation();
  return (
    <article
      className={cn(
        'ph-no-capture relative flex flex-col justify-center rounded-3xl border border-[hsl(36_40%_86%)] bg-[#fffaf0] px-6 pb-10 pt-7 shadow-sm sm:px-8',
        className,
      )}
    >
      <p className="storybook-display whitespace-pre-line text-lg leading-relaxed text-[#20294a] sm:text-xl">{page.text}</p>
      <span className="absolute bottom-3 right-5 text-xs font-medium text-muted-foreground">
        {t('storybookPage.book.pageLabel', { n: page.index + 1 })}
      </span>
    </article>
  );
}

interface StorybookReaderProps {
  book: StorybookBook;
  onMediaError?: () => void;
}

/**
 * Flip-through reader: the cover, then each spread (text + illustration).
 * Prev/next buttons, keyboard arrows and a swipe on touch screens.
 */
export function StorybookReader({ book, onMediaError }: StorybookReaderProps) {
  const { t } = useTranslation();
  const pages = book.pages || [];
  const total = pages.length + 1;
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (delta: number) => setIndex((current) => Math.min(total - 1, Math.max(0, current + delta))),
    [total],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      // Leave arrow keys to an open dialog.
      if (document.querySelector('[role="alertdialog"], [role="dialog"]')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [go]);

  const onTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (event: TouchEvent) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    const end = event.changedTouches[0]?.clientX;
    if (start == null || end == null) return;
    const dx = end - start;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  const page = index > 0 ? pages[index - 1] : null;
  const image = page ? storybookMediaUrl(page.imageUrl) : null;
  const position =
    index === 0
      ? t('storybookPage.book.reader.cover')
      : t('storybookPage.book.reader.position', { n: index, total: pages.length });

  return (
    <section
      aria-roledescription={t('storybookPage.book.reader.roleDescription')}
      aria-labelledby="sb-reader-title"
      className="ph-no-capture"
    >
      <h2 id="sb-reader-title" className="mb-4 text-center text-2xl font-semibold">
        {t('storybookPage.book.reader.title')}
      </h2>
      <div
        className="rounded-[2rem] bg-[linear-gradient(135deg,hsl(45_95%_90%),hsl(330_85%_94%)_50%,hsl(199_90%_92%))] p-3 shadow-[var(--shadow-elevated)] sm:p-5"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          role="group"
          aria-roledescription={t('storybookPage.book.reader.slide')}
          aria-label={position}
          className="min-h-[16rem]"
        >
          {index === 0 ? (
            <StorybookCover book={book} onMediaError={onMediaError} headingLevel={2} className="max-w-lg shadow-none" />
          ) : page ? (
            <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
              <div className="order-2 md:order-1">
                <StorybookPageText page={page} className="h-full min-h-[12rem]" />
              </div>
              <div className="order-1 aspect-square overflow-hidden rounded-3xl bg-card md:order-2">
                {image ? (
                  <img
                    src={image}
                    alt={t('storybookPage.book.reader.illustrationAlt', { n: page.index + 1 })}
                    className="h-full w-full object-cover"
                    onError={onMediaError}
                  />
                ) : (
                  <div aria-hidden="true" className="flex h-full items-center justify-center text-6xl">
                    🎨
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          className="rounded-full bg-card"
          onClick={() => go(-1)}
          disabled={index === 0}
          aria-label={t('storybookPage.book.reader.prev')}
        >
          <ChevronLeft aria-hidden="true" />
          <span className="hidden sm:inline">{t('storybookPage.book.reader.prevShort')}</span>
        </Button>
        <p className="text-sm font-semibold tabular-nums" aria-live="polite">
          {position}
        </p>
        <Button
          type="button"
          className="rounded-full"
          onClick={() => go(1)}
          disabled={index === total - 1}
          aria-label={t('storybookPage.book.reader.next')}
        >
          <span className="hidden sm:inline">{t('storybookPage.book.reader.nextShort')}</span>
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap justify-center gap-0.5">
        {Array.from({ length: total }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={
              i === 0 ? t('storybookPage.book.reader.goToCover') : t('storybookPage.book.reader.goToPage', { n: i })
            }
            aria-current={i === index ? 'true' : undefined}
            className="group flex h-7 min-w-7 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span
              aria-hidden="true"
              className={cn(
                'block h-2.5 rounded-full transition-all',
                i === index ? 'w-5 bg-primary' : 'w-2.5 bg-foreground/25 group-hover:bg-foreground/50',
              )}
            />
          </button>
        ))}
      </div>
      <p className="mt-3 hidden text-center text-xs text-muted-foreground md:block">{t('storybookPage.book.reader.hint')}</p>
    </section>
  );
}
